/**
 * ShareTSOGNRow — the canonical horizontal share-to-social row.
 *
 * Apr 29 2026 follow-up #4 — Lead's call: "Shared buttons for globe
 * should be the same as on the page that says history walked through a
 * methodology page." Extracted from PublicLanding so the same row can
 * be reused on the Time Machine / World view (HUDLayout footer-right)
 * without duplication.
 *
 * Order locked by Lead: X/Twitter, Reddit, LinkedIn, Facebook, Email,
 * Copy Link. Instagram intentionally absent ("link sharing on IG is
 * weak"). Reddit uses an inline Snoo SVG since lucide-react doesn't
 * ship a Reddit icon.
 *
 * Props:
 *   - compact   : when true, renders a small icon-only row (used on the
 *                  Time Machine HUD where space is tight). Default false.
 *   - testIdPrefix : data-testid prefix; defaults to "landing-share" so
 *                     the existing PublicLanding tests keep working.
 */
import { useState } from 'react';
import { Twitter, Linkedin, Facebook, Mail, Link2 } from 'lucide-react';

const RedditIcon = ({ size = 16, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    <circle cx="12" cy="13" r="8" />
    <circle cx="9" cy="13" r="1" fill="currentColor" />
    <circle cx="15" cy="13" r="1" fill="currentColor" />
    <path d="M9 17c1 .8 1.9 1.1 3 1.1s2-.3 3-1.1" />
    <path d="M17 8.5a2 2 0 1 1-1.7 1" />
    <path d="M16 7.5l-3-2" />
  </svg>
);

export default function ShareTSOGNRow({ compact = false, testIdPrefix = 'landing-share', label = 'Share TSOGN' }) {
  // May 01 2026 per Lead: "Also remove intro video for now and link."
  // Share URL was previously `/intro` (the cinematic opening). With the
  // intro removed, social shares now point at the landing page root.
  // When the intro is re-enabled, change back to `${origin}/intro`.
  const shareUrl = (typeof window !== 'undefined') ? `${window.location.origin}/` : 'https://tsogn.com/';
  const shareText = "TSOGN — History walked through a methodology that doesn't blink. Every claim sourced. Every contradiction shown.";
  const shareTitle = 'The Shared Origins Global Network';

  const targets = [
    { key: 'twitter',  label: 'X / Twitter', Icon: Twitter,    href: `https://twitter.com/intent/tweet?${new URLSearchParams({ text: shareText, url: shareUrl, hashtags: 'TSOGN,SharedOrigins' }).toString()}` },
    { key: 'reddit',   label: 'Reddit',      Icon: RedditIcon, href: `https://www.reddit.com/submit?${new URLSearchParams({ url: shareUrl, title: shareTitle }).toString()}` },
    { key: 'linkedin', label: 'LinkedIn',    Icon: Linkedin,   href: `https://www.linkedin.com/sharing/share-offsite/?${new URLSearchParams({ url: shareUrl }).toString()}` },
    { key: 'facebook', label: 'Facebook',    Icon: Facebook,   href: `https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: shareUrl }).toString()}` },
    { key: 'email',    label: 'Email',       Icon: Mail,       href: `mailto:?${new URLSearchParams({ subject: shareTitle, body: `${shareText}\n\n${shareUrl}` }).toString()}` },
  ];

  const [copied, setCopied] = useState(false);
  const copyLink = async () => {
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(shareUrl);
      else {
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        document.body.appendChild(ta);
        ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  const sizeClass = compact
    ? 'inline-flex items-center justify-center w-8 h-8 rounded-full border border-white/20 text-white/65 hover:text-[#D4A84A] hover:border-[#D4A84A]/55 transition-colors'
    : 'inline-flex items-center justify-center w-11 h-11 border border-white/15 rounded-full text-white/70 hover:text-[#D4A84A] hover:border-[#D4A84A]/55 transition-colors';
  const copySizeClass = compact
    ? 'inline-flex items-center justify-center gap-1.5 h-8 px-3 border border-white/20 rounded-full text-white/65 hover:text-[#D4A84A] hover:border-[#D4A84A]/55 transition-colors font-mono text-[10px] tracking-wider uppercase'
    : 'inline-flex items-center justify-center gap-2 h-11 px-4 border border-white/15 rounded-full text-white/70 hover:text-[#D4A84A] hover:border-[#D4A84A]/55 transition-colors font-mono text-xs tracking-wider uppercase';
  const iconSize = compact ? 13 : 16;

  return (
    <div className={compact ? '' : 'mt-12'} data-testid={`${testIdPrefix}-row`}>
      {!compact && label && (
        <div className="font-mono text-xs md:text-sm text-[#D4A84A] tracking-[0.4em] uppercase mb-3">
          {label}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {targets.map(({ key, label: tLabel, Icon, href }) => (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Share TSOGN on ${tLabel}`}
            title={tLabel}
            data-testid={`${testIdPrefix}-${key}`}
            className={sizeClass}
          >
            <Icon size={iconSize} />
          </a>
        ))}
        <button
          onClick={copyLink}
          aria-label="Copy share link"
          title={copied ? 'Copied!' : 'Copy link'}
          data-testid={`${testIdPrefix}-copy`}
          className={copySizeClass}
        >
          <Link2 size={iconSize - 2} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
